import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ExternalLink, ArrowDown } from "lucide-react";
import Logo from "./Logo";

// Zcal direct-iframe embed for "Book a consultation" CTAs.
// Note: Zcal's embed page renders the booking form inside an internal
// scroll container that hides the "Schedule Meeting" button below the fold.
// We surface a clearly visible scroll cue that nudges the user to scroll
// the form area to confirm — bypassing the need to press Tab.
// Source URL: https://zcal.co/i/hiuNkc9D — Audit & Discovery Meeting

const ZCAL_BOOK_URL = "https://zcal.co/i/hiuNkc9D";
const ZCAL_EMBED_URL = `${ZCAL_BOOK_URL}?embed=true`;

export default function BookConsultationModal({ open, onClose }) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [showScrollCue, setShowScrollCue] = useState(false);
  const [cueDismissed, setCueDismissed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    setIframeLoaded(false);
    setShowFallback(false);
    setShowScrollCue(false);
    setCueDismissed(false);

    // If the iframe hasn't loaded in 6s, surface the "Open in new tab" fallback.
    const fallbackTimer = window.setTimeout(() => setShowFallback(true), 6000);

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);

    // Show the scroll cue once the user is likely on the form step.
    // We detect this two ways and use whichever fires first:
    //   1. Window blur events — fired when the user clicks INTO the cross-origin
    //      iframe. After 2 blurs (date → time selection) the form is showing.
    //   2. A 12s safety fallback — if blur tracking is unreliable, we still
    //      surface the cue eventually so the user is guided to the submit button.
    let blurCount = 0;
    let cueTimer;
    const showCue = () => {
      window.clearTimeout(cueTimer);
      setShowScrollCue(true);
    };
    const onBlur = () => {
      blurCount += 1;
      if (blurCount >= 2) {
        cueTimer = window.setTimeout(showCue, 1500);
      }
    };
    window.addEventListener("blur", onBlur);
    const safetyCueTimer = window.setTimeout(showCue, 12000);

    document.body.style.overflow = "hidden";

    return () => {
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(cueTimer);
      window.clearTimeout(safetyCueTimer);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="book-consultation-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="book-consultation-title"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-[6px]"
            onClick={onClose}
            data-testid="book-consultation-backdrop"
          />

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[1000px] h-[calc(100vh-3rem)] sm:h-[860px] max-h-[92vh] rounded-[24px] overflow-hidden bg-white border border-black/5 shadow-[0_50px_140px_-30px_rgba(0,0,0,0.4)]"
          >
            <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-5 py-3 bg-white/90 backdrop-blur-md border-b border-black/5">
              <div className="flex items-center gap-3 min-w-0">
                <Logo asLink={false} height={20} testId="book-modal-logo" />
                <span className="h-5 w-px bg-black/10 shrink-0" />
                <div className="min-w-0">
                  <div
                    id="book-consultation-title"
                    className="font-display text-[#0A0A0A] text-[14px] font-medium truncate"
                  >
                    Audit &amp; Discovery Meeting
                  </div>
                  <div className="text-[11px] text-black/50 truncate">
                    30-min session with our strategists · powered by zcal
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={ZCAL_BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-consultation-newtab"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-black/55 hover:text-[#0A0A0A] hover:bg-black/5 transition-colors"
                  aria-label="Open booking in a new tab"
                >
                  <ExternalLink size={12} strokeWidth={2} />
                  Open in new tab
                </a>
                <button
                  onClick={onClose}
                  data-testid="book-consultation-close"
                  aria-label="Close"
                  className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
                >
                  <X size={16} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="absolute inset-0 pt-16 bg-white">
              {!iframeLoaded && (
                <div className="absolute inset-0 pt-16 flex flex-col items-center justify-center gap-4 pointer-events-none px-6 text-center">
                  <span className="h-7 w-7 rounded-full border-2 border-black/10 border-t-[#FFB000] animate-spin" />
                  <div className="text-sm text-black/55">Loading calendar…</div>
                  {showFallback && (
                    <a
                      href={ZCAL_BOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="book-consultation-fallback"
                      className="pointer-events-auto mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB000] hover:bg-[#FFA000] text-[#0A0A0A] text-sm font-semibold transition-colors"
                    >
                      <ExternalLink size={13} strokeWidth={2.4} />
                      Open booking in a new tab
                    </a>
                  )}
                </div>
              )}
              <iframe
                key={open ? "open" : "closed"}
                title="Audit & Discovery Meeting"
                src={ZCAL_EMBED_URL}
                onLoad={() => setIframeLoaded(true)}
                data-testid="zcal-iframe"
                className="w-full h-full border-0 bg-white block"
                allow="payment; fullscreen"
              />
            </div>

            {/* Scroll cue — Zcal's form has its own internal scroll that hides
                the Schedule Meeting button. This pill nudges the user to scroll
                inside the form area when filling it out. Appears only after
                the user has interacted with the iframe twice (date + time
                selection) and can be dismissed. */}
            {iframeLoaded && showScrollCue && !cueDismissed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.4 }}
                data-testid="book-consultation-scroll-cue"
                className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-full bg-[#0A0A0A]/90 text-white text-[12px] font-medium backdrop-blur-md shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]"
              >
                <ArrowDown size={13} strokeWidth={2.4} className="animate-bounce" />
                <span className="hidden sm:inline">Scroll inside the form to reach &ldquo;Schedule Meeting&rdquo;</span>
                <span className="sm:hidden">Scroll for &ldquo;Schedule Meeting&rdquo;</span>
                <button
                  onClick={() => setCueDismissed(true)}
                  data-testid="book-consultation-scroll-cue-dismiss"
                  aria-label="Dismiss hint"
                  className="ml-1 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <X size={12} strokeWidth={2.4} />
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
